import CHANNELS from '../../../../constants/Channels'
import OUConditionType from '../../../../constants/offer_up/ConditionTypes'
import { PICKUP_CARRIER } from '../../../../constants/orders/Carriers'
import { isShopifyAccountCAD } from '../../../../utils/marketAccount'
import { getShopifyRequiredTags, getShopifyTags } from '../../../../utils/tags'
import { Condition, Gender } from './FacebookCard/constants'
import { USE_PRODUCT_VALUE } from './Helpers'

// use whole number if no decimals, otherwise, tack on monetary decimal
export const monetaryValue = str => {
  const value = str && parseFloat(str)
  if (!value) return null

  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)
}

const extractConfigurationValue = (configuration, key) =>
  configuration && configuration[`${key}By`] === 'SET' ? configuration[key] : null

function safeProfileId(profiles, type) {
  const found = profiles?.find(p => p.profileType === type && p.default)
  return found || {}
}

export default class ListingDefaultResolver {
  private marketSettings: any

  private category: any

  private carrierId: number | string

  private cmcMap: any

  constructor(marketSettings, category, carrierId) {
    this.marketSettings = marketSettings || {}
    this.category = category
    this.carrierId = carrierId
    this.cmcMap =
      category?.categoryMappings
        ?.map(m => m.categoryMarketCategory)
        .reduce((acc, curr) => ({ [curr.marketId]: curr, ...acc }), {}) ?? {}
  }

  getBlankListing(configuration) {
    switch (configuration.marketId) {
      case CHANNELS.SHOPGOODWILL.id:
        return this.getBlankShopgoodwillListing(configuration)
      case CHANNELS.EBAY.id:
        return this.getBlankEbayListing(configuration)
      case CHANNELS.OFFER_UP.id:
        return this.getBlankOfferUpListing(configuration)
      case CHANNELS.FACEBOOK.id:
        return this.getBlankFacebookListing(configuration)
      case CHANNELS.SHOPIFY.id:
        return this.getBlankShopifyListing(configuration)
      case CHANNELS.GOODWILLFINDS.id:
        return this.getBlankGoodwillfindsListing(configuration)
      case CHANNELS.ETSY.id:
        return this.getBlankEtsyListing(configuration)
      case CHANNELS.DEPOP.id:
        return this.getBlankDepopListing(configuration)
      default:
        return {}
    }
  }

  // market-specific listing configurations

  getBlankShopgoodwillListing(configuration) {
    const settings = this.marketSettings.shopgoodwill || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'
    const buyNowPrice = monetaryValue(extractConfigurationValue(configuration, 'buyItNowPriceChange')) || '0'

    let startingBid = extractConfigurationValue(configuration, 'priceChange') || settings.defaultStartingPrice

    // Concurrent Shopgoodwill listings must be "buy now" listings.
    // A regular listing with a buyNowPrice must have a startingBid value, if priceChange was not set for the strategy
    // assume a buy now listing, but allow the user to make it an auction with a buy now value.
    const setStartingBidToBuyNowPrice =
      parseFloat(buyNowPrice) > 0 && [null, undefined].includes(configuration.priceChange)
    if (isConcurrent || setStartingBidToBuyNowPrice) {
      startingBid = buyNowPrice
    }

    return {
      categoryMarketCategoryId:
        (this.cmcMap[CHANNELS.SHOPGOODWILL.id] && this.cmcMap[CHANNELS.SHOPGOODWILL.id].id) || null,
      sellerPrivateDescription: settings.sendSkuAsPrivateDescription === true ? USE_PRODUCT_VALUE : '',
      startingBid,
      auctionDuration: isConcurrent
        ? 180
        : (configuration && configuration.listingDuration) || settings.defaultAuctionDuration,
      isConcurrent,
      auctionStart: settings.defaultAuctionStart,
      bidIncrement: monetaryValue(settings.defaultBidIncrement) || '1',
      noCombineShipping: settings.defaultNoCombineShipping,
      buyNowPrice,
      stockQuantity: '0',
      reservePrice: monetaryValue(extractConfigurationValue(configuration, 'reservePriceChange')) || '0',
      shippingPrice:
        monetaryValue(
          extractConfigurationValue(configuration, 'shippingPriceChange') || settings.defaultShippingPrice
        ) || '0',
      handlingPrice:
        monetaryValue(
          extractConfigurationValue(configuration, 'handlingPriceChange') || settings.defaultHandlingPrice
        ) || '0'
    }
  }

  getBlankEbayListing(configuration) {
    const cmc = this.cmcMap[CHANNELS.EBAY.id]
    const settings = this.marketSettings.ebay || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'
    const marketAccountId = configuration.marketAccountId || settings.marketAccountId

    let scheduleTime = null

    const defaultLocalScheduleTime = settings.defaultLocalScheduleTime ?? settings.defaultScheduleTime

    if (defaultLocalScheduleTime) {
      scheduleTime = new Date()
      const timeArray = defaultLocalScheduleTime.split(':')
      scheduleTime.setHours(timeArray[0])
      scheduleTime.setMinutes(timeArray[1])
      if (scheduleTime <= new Date()) scheduleTime.setDate(scheduleTime.getDate() + 1)
    }
    const policies = configuration.policies || {}
    return {
      paymentProfileId: policies.paymentProfileId || safeProfileId(settings.defaultProfiles, 'PAYMENT').externalId,
      returnProfileId: policies.returnProfileId || safeProfileId(settings.defaultProfiles, 'RETURN_POLICY').externalId,
      shippingProfileId: policies.shippingProfileId || safeProfileId(settings.defaultProfiles, 'SHIPPING').externalId,
      specifics: {},
      isConcurrent,
      marketAccountId,
      categoryMarketCategoryId: (cmc && cmc.id) || null,
      categoryMarketCategory: cmc || null,
      productListingDetails: {},
      reservePrice: monetaryValue(extractConfigurationValue(configuration, 'reservePriceChange')),
      startingPrice: monetaryValue(
        extractConfigurationValue(configuration, 'priceChange') || settings.defaultStartingPrice
      ),
      shippingPrice: monetaryValue(
        extractConfigurationValue(configuration, 'shippingPriceChange') || settings.defaultShippingPrice
      ),
      buyItNowPrice: monetaryValue(extractConfigurationValue(configuration, 'buyItNowPriceChange')),
      scheduleTime,
      listingType: configuration.listingType || settings.defaultListingType,
      listingDuration: configuration.listingDuration || settings.defaultListingDuration,
      dispatchTimeMax: settings.defaultHandlingTime,
      currencyCode: settings.currencyCode,
      useTaxTable: settings.enableTaxTable ? false : null,
      quantity: 1
    }
  }

  getBlankFacebookListing(configuration) {
    const settings = this.marketSettings.facebook || {}
    const cmc = this.cmcMap[CHANNELS.FACEBOOK.id]
    return {
      categoryMarketCategory: cmc || null,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      condition: Condition.USED,
      facebookPageFeedId: configuration.facebookPageFeed || settings.defaultFacebookPageFeedId,
      gender: Gender.UNISEX,
      facebookPageFeed: null,
      duration: configuration.listingDuration || settings.defaultDuration || -1
    }
  }

  getBlankOfferUpListing(configuration) {
    const settings = this.marketSettings.offerUp || {}
    const cmc = this.cmcMap[CHANNELS.OFFER_UP.id]
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'

    return {
      categoryMarketCategoryId: (cmc && cmc.id) || null,
      isConcurrent,
      listingType: settings.defaultListingType,
      listingDuration: configuration.listingDuration || null,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      condition: OUConditionType.Used,
      shippingPrice: monetaryValue(
        extractConfigurationValue(configuration, 'shippingPriceChange') || settings.defaultShippingPrice
      )
    }
  }

  getBlankShopifyListing(configuration) {
    const settings = this.marketSettings.shopify || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'
    const usePickup = this.carrierId === PICKUP_CARRIER.id

    return {
      quantity: 1,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      compareAtPrice: undefined,
      productType: USE_PRODUCT_VALUE,
      barcode: undefined,
      templateSuffix: undefined,
      duration: isConcurrent ? null : configuration.listingDuration || null,
      isConcurrent,
      // This is also reset in JoinedForm ANY TIME the carrier changes
      locationId: usePickup ? settings.defaultPickupLocationId : settings.defaultShippingLocationId,
      tags: getShopifyTags(
        configuration.shopifyTags,
        (settings && settings.productCategoryAsTags) || false,
        null,
        this.category.pathCache
      ),
      requiredTags: getShopifyRequiredTags(
        (settings && settings.productCategoryAsTags) || false,
        this.category.pathCache
      ),
      rawCollections: '', // csv of options, split in ProductSerializer to collections
      options: [],
      marketAccountId: settings.marketAccountId,
      useCAD: settings && isShopifyAccountCAD(settings.marketAccount),
      useProductCategoryAsTags: settings && settings.productCategoryAsTags
    }
  }

  getBlankGoodwillfindsListing(configuration) {
    const settings = this.marketSettings.goodwillfinds || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'

    return {
      quantity: 1,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      compareAtPrice: undefined,
      duration: isConcurrent ? null : configuration.listingDuration || null,
      isConcurrent,
      // supplierFromProduct: get from product some how, see: https://app.asana.com/0/1206184798330168/1206911603181159/f
      categoryMarketCategoryId:
        (this.cmcMap[CHANNELS.GOODWILLFINDS.id] && this.cmcMap[CHANNELS.GOODWILLFINDS.id].id) || null
    }
  }

  getBlankEtsyListing(configuration) {
    const settings = this.marketSettings.etsy || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'

    return {
      quantity: 1,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      duration: isConcurrent ? null : configuration.listingDuration || null,
      isConcurrent,
      returnPolicyId: configuration?.policies?.returnProfileId,
      shippingProfileId: configuration?.policies?.shippingProfileId,
      categoryMarketCategoryId: (this.cmcMap[CHANNELS.ETSY.id] && this.cmcMap[CHANNELS.ETSY.id].id) || null
    }
  }

  // Condition is deliberately left unset — there is no sensible default and it is required by Depop.
  getBlankDepopListing(configuration) {
    const settings = this.marketSettings.depop || {}
    const isConcurrent = configuration && configuration.stepType === 'CONCURRENT'

    return {
      quantity: 1,
      duration: isConcurrent ? null : configuration.listingDuration || null,
      isConcurrent,
      price: monetaryValue(extractConfigurationValue(configuration, 'priceChange') || settings.defaultPrice),
      categoryMarketCategoryId: (this.cmcMap[CHANNELS.DEPOP.id] && this.cmcMap[CHANNELS.DEPOP.id].id) || null
    }
  }
}
